import geojson from 'geojson';
import merge from 'lodash/merge';
import React from 'react';

import MapContent, {SourceRecord} from './MapContent';

const DEFAULT_LAYER_CONFIGS: LayerConfig[] = [
  {type: 'fill', layout: {}, paint: {'fill-color': '#FFF', 'fill-opacity': 0.8}},
  {type: 'line', layout: {}, paint: {'line-color': '#FFF', 'line-opacity': 1, 'line-width': 1}},
  {
    type: 'symbol',
    layout: {},
    paint: {'text-color': '#FFF'},
  },
  {
    type: 'circle',
    filter: ['==', '$type', 'Point'],
    layout: {},
    paint: {'circle-color': '#FFF', 'circle-radius': 5},
  },
];

type LayerConfig =
  | Omit<mapboxgl.FillLayerSpecification, 'id' | 'source'>
  | Omit<mapboxgl.LineLayerSpecification, 'id' | 'source'>
  | Omit<mapboxgl.SymbolLayerSpecification, 'id' | 'source'>
  | Omit<mapboxgl.CircleLayerSpecification, 'id' | 'source'>;

interface Props {
  idPrefix: string;
  map: mapboxgl.Map;
  isMapLoaded: boolean;

  geoJson?: geojson.FeatureCollection | geojson.Feature;
  layerConfigs?: LayerConfig[];
}

const GeoJsonLayersVector: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  idPrefix,
  map,
  isMapLoaded,
  geoJson,
  layerConfigs = [] as LayerConfig[],
}) => {
  const sourceId = `GeoJsonLayersVector-${idPrefix}`;

  const sources: SourceRecord[] = React.useMemo(() => {
    return geoJson
      ? [
          {
            id: sourceId,
            source: {
              type: 'geojson',
              data: geoJson,
            },
          },
        ]
      : [];
  }, [geoJson, sourceId]);

  const layers: mapboxgl.LayerSpecification[] = React.useMemo(() => {
    return layerConfigs.map((layerConfig, i) => {
      const defaultConfig = DEFAULT_LAYER_CONFIGS.find((c) => c.type === layerConfig.type);
      const mergedConfig = merge({}, defaultConfig, layerConfig);

      return {
        id: `${sourceId}-${layerConfig.type}-${i}`,
        source: sourceId,
        ...mergedConfig,
      };
    });
  }, [layerConfigs, sourceId]);

  return <MapContent map={map} isMapLoaded={isMapLoaded} sources={sources} layers={layers} />;
};

export default GeoJsonLayersVector;
